import { useUpdateUserMutation } from "@hooks";
import { OrgScopedBaseUser } from "@models";
import { useEffect, useState } from "react";
import { useSnackbar } from "src/components/Snackbar";
import {
  useAddFunctionalGroupMemberMutation,
  useRemoveFunctionalGroupMemberMutation,
} from "src/hooks/mutations";
import { FunctionalGroup } from "src/types";
import { getOrgMembership } from "src/utils/user";

export const emptyUser = { firstName: "", lastName: "" };

export type ChangeUser = {
  firstName: string;
  lastName: string;
};

export type UpdateUserDataProps = {
  updateUserDataResponse: UpdateUserDataResponse;
};

export type UpdateUserDataResponse = {
  openUpdateUserModal: boolean;
  setOpenUpdateUserModal: (open: boolean) => void;
  userToUpdate: ChangeUser;
  setUserToUpdate: (userToUpdate: ChangeUser) => void;
  selectedFunctionalGroupIds: string[];
  setSelectedFunctionalGroupIds: (functionalGroupIds: string[]) => void;
  editableFunctionalGroups: FunctionalGroup[];
  areAllUpdateUserInputsFilled: boolean;
  handleUpdateUser: ({ userId }: { userId: string }) => Promise<void>;
  handleCloseUpdateUserModal: () => void;
  selectedUser: OrgScopedBaseUser | undefined;
  setSelectedUser: (selectedUser: OrgScopedBaseUser | undefined) => void;
  isUpdatingUser: boolean;
};

export default function useUpdateUserData({
  orgId,
  editableFunctionalGroups = [],
}: {
  orgId: string;
  editableFunctionalGroups?: FunctionalGroup[];
}): UpdateUserDataProps {
  const [openUpdateUserModal, setOpenUpdateUserModal] = useState(false);
  const [userToUpdate, setUserToUpdate] = useState<ChangeUser>(emptyUser);
  const [selectedFunctionalGroupIds, setSelectedFunctionalGroupIds] = useState<
    string[]
  >([]);
  const [selectedUser, setSelectedUser] = useState<OrgScopedBaseUser | undefined>(
    undefined
  );
  const { showSnackbar } = useSnackbar();

  const patchUserMutation = useUpdateUserMutation(orgId);
  const addFunctionalGroupMemberMutation =
    useAddFunctionalGroupMemberMutation();
  const removeFunctionalGroupMemberMutation =
    useRemoveFunctionalGroupMemberMutation();

  useEffect(() => {
    if (!selectedUser) {
      setUserToUpdate(emptyUser);
      setSelectedFunctionalGroupIds([]);
      return;
    }

    const orgMembership = getOrgMembership(selectedUser, orgId);
    const memberships = orgMembership?.functionalGroupMemberships ?? [];

    setUserToUpdate({
      firstName: selectedUser.firstName ?? "",
      lastName: selectedUser.lastName ?? "",
    });
    setSelectedFunctionalGroupIds(
      memberships.map((membership: any) => membership.functionalGroupId)
    );
  }, [selectedUser, orgId]);

  const handleCloseUpdateUserModal = () => {
    setOpenUpdateUserModal(false);
    setSelectedUser(undefined);
    setUserToUpdate(emptyUser);
    setSelectedFunctionalGroupIds([]);
  };

  const handleUpdateUser = async ({ userId }: { userId: string }) => {
    try {
      const waitMutation = await patchUserMutation.mutateAsync({
        id: userId,
        user: userToUpdate,
      });

      const isMutationSuccessful = waitMutation.status === 200;

      if (!isMutationSuccessful) {
        showSnackbar("Failed to update user");
        return;
      }

      const orgMembership = getOrgMembership(selectedUser, orgId);

      if (orgMembership) {
        const currentMemberships = orgMembership.functionalGroupMemberships ?? [];
        const currentGroupIds = new Set(
          currentMemberships.map((membership: any) => membership.functionalGroupId)
        );
        const nextGroupIds = new Set(selectedFunctionalGroupIds);

        await Promise.all(
          currentMemberships
            .filter(
              (membership: any) => !nextGroupIds.has(membership.functionalGroupId)
            )
            .map((membership: any) =>
              removeFunctionalGroupMemberMutation.mutateAsync({
                orgId,
                groupId: membership.functionalGroupId,
                membershipId: membership.id,
              })
            )
        );

        await Promise.all(
          selectedFunctionalGroupIds
            .filter((groupId) => !currentGroupIds.has(groupId))
            .map((groupId) =>
              addFunctionalGroupMemberMutation.mutateAsync({
                orgId,
                groupId,
                organizationMembershipId: orgMembership.id,
              })
            )
        );
      }

      handleCloseUpdateUserModal();
    } catch {
      showSnackbar("Failed to update user");
    }
  };

  const areAllUpdateUserInputsFilled =
    !!userToUpdate?.firstName && !!userToUpdate?.lastName;
  const isUpdatingUser =
    patchUserMutation.isPending ||
    addFunctionalGroupMemberMutation.isPending ||
    removeFunctionalGroupMemberMutation.isPending;

  return {
    updateUserDataResponse: {
      openUpdateUserModal,
      setOpenUpdateUserModal,
      userToUpdate,
      setUserToUpdate,
      selectedFunctionalGroupIds,
      setSelectedFunctionalGroupIds,
      editableFunctionalGroups,
      areAllUpdateUserInputsFilled,
      handleUpdateUser,
      handleCloseUpdateUserModal,
      selectedUser,
      setSelectedUser,
      isUpdatingUser,
    },
  };
}
